import React, { useState } from 'react';
import { Head } from 'vite-react-ssg';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import Header from '../components/Header';
import Footer from '../components/Footer';
import BackToTop from '../components/BackToTop';
import CustomCursor from '../components/CustomCursor';
import { ArrowUpRight } from 'lucide-react';

const Events = () => {
  const [darkMode, setDarkMode] = useState(false);
  const [hoveredIndex, setHoveredIndex] = useState(null);
  const navigate = useNavigate();
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.04 });

  const toggleDarkMode = () => {
    setDarkMode(!darkMode);
    document.documentElement.classList.toggle('dark');
  };

  const silk = [0.25, 0.46, 0.45, 0.94];
  const expo = [0.16, 1, 0.3, 1];

  const eyebrowV = { hidden:{opacity:0,y:14}, visible:{opacity:1,y:0,transition:{duration:0.7,ease:silk}} };
  const wordV    = { hidden:{opacity:0,y:44,rotateX:14}, visible:{opacity:1,y:0,rotateX:0,transition:{duration:0.8,ease:expo}} };
  const italicV  = { hidden:{opacity:0,x:-36,skewX:-5}, visible:{opacity:1,x:0,skewX:0,transition:{duration:0.7,ease:expo}} };
  const idxNumV  = { hidden:{opacity:0,y:10}, visible:{opacity:1,y:0,transition:{duration:0.9,ease:silk}} };
  const idxLineV = { hidden:{scaleX:0,opacity:0}, visible:{scaleX:1,opacity:1,transition:{duration:1.1,ease:expo}} };
  const cardV    = { hidden:{opacity:0,y:48,scale:0.97}, visible:{opacity:1,y:0,scale:1,transition:{duration:1.2,ease:expo}} };
  const footHrLV = { hidden:{scaleX:0,opacity:0}, visible:{scaleX:1,opacity:1,transition:{duration:1.2,ease:expo}} };
  const footHrRV = { hidden:{scaleX:0,opacity:0}, visible:{scaleX:1,opacity:1,transition:{duration:1.2,ease:expo}} };
  const footLblV = { hidden:{opacity:0,y:6}, visible:{opacity:1,y:0,transition:{duration:1.0,ease:silk,delay:0.2}} };

  return (
    <div className={darkMode ? 'dark' : ''}>
      
<Head>
  {/* ── Basic Meta ── */}
  <title>VibrantViva Events | Business Growth, Learning and Success Events</title>
  <meta
    name="description"
    content="Explore VibrantViva events designed to inspire growth, leadership, and entrepreneurship through training, networking, and recognition."
  />
  <meta
    name="keywords"
    content="Entrepreneur Events, Business Networking Events, Business Growth Events, entrepreneurship training programs, entrepreneur learning programs, Leadership Development, Professional Growth, Entrepreneurship, Entrepreneur Network, leadership development in business"
  />
  <meta name="author" content="VibrantViva" />
  <meta name="robots" content="index, follow" />
  <link rel="canonical" href="https://vibrantviva.com/events" />

  {/* ── Open Graph (Facebook / WhatsApp / LinkedIn) ── */}
  <meta property="og:type" content="website" />
  <meta property="og:url" content="https://vibrantviva.com/events" />
  <meta
    property="og:title"
    content="VibrantViva Events | Business Growth, Learning & Success Events"
  />
  <meta
    property="og:description"
    content="Explore VibrantViva events designed to inspire growth, leadership, and entrepreneurship through training, networking, and recognition."
  />
  <meta property="og:image" content="https://vibrantviva.com/assets/logo.png" />
  <meta property="og:image:width" content="200" />
  <meta property="og:image:height" content="200" />
  <meta property="og:site_name" content="VibrantViva" />
  <meta property="og:locale" content="en_IN" />

  {/* ── JSON-LD Structured Data ── */}
  <script
  type="application/ld+json"
  dangerouslySetInnerHTML={{
    __html: JSON.stringify({
      "@context": "https://schema.org",
      "@type": "Organization",
      "name": "VibrantViva",
      "url": "https://vibrantviva.com/events",
      "logo": "https://vibrantviva.com/assets/logo.png",
      "description":
        "Explore VibrantViva events designed to inspire growth, leadership, and entrepreneurship through training, networking, and recognition.",
      "sameAs": [
        "https://www.facebook.com/media/set/?vanity=officialVibrantViva&set=a.705168821617957",
        "https://www.instagram.com/vibrantvivaofficial/",
        "https://www.linkedin.com/company/indusviva-healthsciences-private-limited/posts/?feedView=all&viewAsMember=true",
        "https://x.com/IndusvivaPvt?t=yf_P18W0HW6DR9ZbrhjRzg&s=09",
        "https://www.youtube.com/@VibrantViva"
      ],
      "contactPoint": {
        "@type": "ContactPoint",
        "telephone": "+91-1800-103-4916",
        "contactType": "Customer Support",
        "hoursAvailable": [
          {
            "@type": "OpeningHoursSpecification",
            "dayOfWeek": [
              "Monday",
              "Tuesday",
              "Wednesday",
              "Thursday",
              "Friday"
            ],
            "opens": "09:30",
            "closes": "18:00"
          },
          {
            "@type": "OpeningHoursSpecification",
            "dayOfWeek": ["Saturday"],
            "opens": "09:30",
            "closes": "13:30"
          }
        ],
        "availableLanguage": [
          "English",
          "Hindi",
          "Kannada",
          "Tamil",
          "Telugu",
          "Malayalam"
        ],
        "areaServed": "IN",
        "contactOption": "TollFree"
      },
      "address": {
        "@type": "PostalAddress",
        "streetAddress":
          "No 92-1-36, Viva Tower, Nandi Durga Road, Jayamahal Extension",
        "addressLocality": "Bangalore Urban",
        "addressRegion": "Karnataka",
        "postalCode": "560046",
        "addressCountry": "IN"
      },
      "location": {
        "@type": "Place",
        "name": "Viva Tower",
        "address": {
          "@type": "PostalAddress",
          "streetAddress":
            "No 92-1-36, Viva Tower, Nandi Durga Road, Jayamahal Extension",
          "addressLocality": "Bangalore Urban",
          "addressRegion": "Karnataka",
          "postalCode": "560046",
          "addressCountry": "IN"
        }
      }
    })}
  }
  />
</Head>


      <style dangerouslySetInnerHTML={{ __html: `
        @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,400;0,500;0,600;1,400;1,500&family=Outfit:wght@300;400;500;600&display=swap');

        *, *::before, *::after { margin:0; padding:0; box-sizing:border-box; }
        body { background:#0B1628; }
        .events-root {
          font-family:'Outfit', sans-serif;
          background: linear-gradient(
            to bottom,
            #0B1628 0%, #0F2040 12%, #1A3A5C 24%,
            #2D5E82 36%, #6A9DBF 48%, #C8DCF0 58%, #F0F6FA 100%
          );
          position:relative; min-height:100vh;
        }
        .events-inner { position:relative; z-index:1; }

        .section-eyebrow {
          font-family:'Outfit', sans-serif; font-size:0.6rem; font-weight:500;
          letter-spacing:0.3em; text-transform:uppercase; color:#7AAAC8;
          display:flex; align-items:center; gap:12px;
        }
        .eyebrow-line { display:inline-block; width:32px; height:1px; background:#3A6A90; }

        .heading-primary {
          font-family:'Cormorant Garamond', serif; font-weight:500;
          letter-spacing:-0.015em; line-height:1.15; color:#E8F2FA; perspective:700px;
        }
        .heading-italic {
          font-family:'Cormorant Garamond', serif; font-style:italic; font-weight:400;
          color:#6AAAD0; display:block; margin-top:4px;
        }

        .index-num {
          font-family:'Cormorant Garamond', serif; font-style:italic;
          font-size:0.82rem; color:#fff;
        }
        .index-hr { flex:1; height:1px; background:rgba(255,255,255,0.08); transform-origin:left center; }

        /* ═══ Cards row — 3 across ═══ */
        .cards-row {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 24px;
        }

        .card-col-landscape,
        .card-col-reel { width: 100%; }

        .event-card {
          position: relative;
          width: 100%;
          overflow: hidden;
          border-radius: 14px;
          cursor: pointer;
          border: 1px solid rgba(255,255,255,0.1);
          background: #08101e;
          transition:
            border-color 0.7s ease,
            box-shadow 0.7s ease,
            transform 0.6s cubic-bezier(0.25,0.46,0.45,0.94);
        }
        .event-card:hover {
          border-color: rgba(255,255,255,0.26);
          box-shadow: 0 28px 72px rgba(0,15,40,0.6);
          transform: translateY(-6px);
        }

        /* Square (1:1 / 1080x1080-style) cards */
        .event-card-landscape,
        .event-card-reel { padding-top: 100%; }

        .video-landscape {
          position: absolute;
          top: 0; height: 100%;
          width: 177.78%;
          left: 50%;
          transform: translateX(-50%);
          border: none; pointer-events: none;
          transition: filter 1.4s ease;
          filter: brightness(0.92) saturate(0.95);
        }
        .event-card:hover .video-landscape { filter: brightness(0.5) saturate(1.05); }

        .video-reel-wrap {
          position: absolute;
          inset: 0;
          overflow: hidden;
          pointer-events: none;
          transition: filter 1.4s ease;
          filter: brightness(0.92) saturate(0.95);
        }
        .event-card:hover .video-reel-wrap { filter: brightness(0.5) saturate(1.05); }
        .video-reel-wrap iframe {
          position: absolute;
          inset: 0;
          width: 100%;
          height: 100%;
          border: none;
          pointer-events: none;
        }

        .event-card iframe { pointer-events: none; }

        .card-overlay {
          position:absolute; inset:0; z-index:2;
          background: linear-gradient(to top, rgba(4,10,24,0.95) 0%, rgba(4,10,24,0.12) 50%, transparent 100%);
        }
        .card-click-shield { position:absolute; inset:0; z-index:3; }

        .card-ghost-num {
          position:absolute; right:-8px; bottom:-24px;
          font-family:'Cormorant Garamond', serif; font-style:italic;
          font-weight:700; line-height:1;
          color:rgba(255,255,255,0.04); pointer-events:none; user-select:none; z-index:4;
          transition: color 0.7s ease, transform 0.7s cubic-bezier(0.25,0.46,0.45,0.94);
        }
        .event-card-landscape .card-ghost-num { font-size:10rem; }
        .event-card-reel      .card-ghost-num { font-size:7rem; }
        .event-card:hover .card-ghost-num { color:rgba(255,255,255,0.08); transform:translateY(-8px); }

        .card-shine {
          position:absolute; bottom:0; left:0; right:0; height:1px; z-index:4;
          background:linear-gradient(to right, transparent, rgba(160,210,255,0.55), transparent);
          opacity:0; transition:opacity 0.6s ease;
        }
        .event-card:hover .card-shine { opacity:1; }

        .card-content {
          position:absolute; inset:0; z-index:5;
          display:flex; flex-direction:column; justify-content:flex-end;
          pointer-events:none;
        }
        .card-content-inner { padding:26px; }

        .card-year {
          font-family:'Outfit', sans-serif; font-size:0.58rem;
          letter-spacing:0.28em; text-transform:uppercase;
          color:rgba(180,215,245,0.4); font-weight:400;
          transition:color 0.6s ease;
        }
        .event-card:hover .card-year { color:rgba(180,215,245,0.85); }

        .card-sep {
          height:1px; width:36px; background:rgba(160,210,255,0.35); margin:10px 0;
          transition:width 0.7s cubic-bezier(0.25,0.46,0.45,0.94);
        }
        .event-card:hover .card-sep { width:60px; }

        .card-title-lg {
          font-family:'Cormorant Garamond', serif; font-weight:500;
          font-size:2rem; line-height:1.05; letter-spacing:-0.02em; color:#EEF4FA;
          transition:letter-spacing 0.6s ease;
        }
        .card-title-sm {
          font-family:'Cormorant Garamond', serif; font-weight:500;
          font-size:1.5rem; line-height:1.1; letter-spacing:-0.02em; color:#EEF4FA;
          transition:letter-spacing 0.6s ease;
        }
        .event-card:hover .card-title-lg,
        .event-card:hover .card-title-sm { letter-spacing:-0.012em; }

        .card-cta-row { display:flex; align-items:center; justify-content:space-between; margin-top:18px; }
        .card-cta-text {
          font-family:'Outfit', sans-serif; font-size:0.6rem;
          letter-spacing:0.22em; text-transform:uppercase; font-weight:400;
          color:rgba(180,215,245,0.35);
          transition:color 0.6s ease, letter-spacing 0.6s ease;
        }
        .event-card:hover .card-cta-text { color:rgba(210,235,255,0.9); letter-spacing:0.28em; }

        .cta-circle {
          width:34px; height:34px; border-radius:50%;
          border:1px solid rgba(180,215,245,0.2);
          display:flex; align-items:center; justify-content:center;
          background:rgba(255,255,255,0.06);
          transition:transform 0.7s cubic-bezier(0.34,1.56,0.64,1), background 0.5s, border-color 0.5s;
        }
        .event-card:hover .cta-circle {
          transform:rotate(45deg);
          background:rgba(255,255,255,0.14);
          border-color:rgba(180,215,245,0.5);
        }

        .foot-hr { flex:1; height:1px; background:rgba(255,255,255,0.08); }
        .foot-hr-left  { transform-origin:left center; }
        .foot-hr-right { transform-origin:right center; }
        .foot-label {
          font-family:'Outfit', sans-serif; font-size:0.55rem;
          letter-spacing:0.3em; text-transform:uppercase;
          color:rgba(160,200,235,0.28);
        }

        @media (max-width:900px) {
          .cards-row { grid-template-columns: 1fr; }
        }
      `}} />

      <CustomCursor />

        <Header darkMode={darkMode} toggleDarkMode={toggleDarkMode} />


      {/* Preload hidden iframe */}
      <div style={{position:'fixed',top:'-9999px',left:'-9999px',width:'1px',height:'1px',pointerEvents:'none',opacity:0,zIndex:-1}}>
        <iframe
          src="https://player.vimeo.com/video/1170646454?badge=0&autopause=0&player_id=0&app_id=58479&autoplay=1&muted=1&loop=1&background=1&controls=0"
          allow="autoplay; fullscreen; picture-in-picture"
          title="India preload"
        />
      </div>

      <section className="events-root py-20">
        <div ref={ref} className="events-inner container mx-auto px-5 max-w-[88rem]">
        
          {/* ── Header ── */}
          <div className="mt-24 mb-14">
            <motion.p
              className="section-eyebrow mb-5 mt-10"
              variants={eyebrowV} initial="hidden" animate={inView ? 'visible' : 'hidden'}
            >
              <span className="eyebrow-line" />
              Our Programmes
            </motion.p>
            
            {/* Heading + divider + desc — Statutory-style alignment */}
            <div className="flex items-end gap-7">
              <motion.h2
                className="heading-primary text-4xl md:text-5xl"
                variants={{ hidden:{}, visible:{ transition:{ staggerChildren:0.12, delayChildren:0.18 } } }}
                initial="hidden" animate={inView ? 'visible' : 'hidden'}
              >
                <span style={{ display:'flex', flexWrap:'wrap', gap:'0 0.3em' }}>
                  {['Events','We','Do'].map((w) => (
                    <motion.span key={w} variants={wordV} style={{ display:'inline-block' }}>{w}</motion.span>
                  ))}
                </span>
                <motion.span className="heading-italic" variants={italicV}>Every Year</motion.span>
              </motion.h2>

              {/* Vertical divider */}
              <motion.div
                className="hidden md:block"
                style={{ width:1, alignSelf:'stretch', background:'linear-gradient(to bottom, transparent, rgba(106,170,208,0.4), transparent)', marginBottom:4 }}
                initial={{ opacity:0, scaleY:0.5 }}
                animate={inView ? { opacity:1, scaleY:1 } : {}}
                transition={{ duration:0.7, ease:expo, delay:0.35 }}
              />

              {/* Desc para - desktop */}
              <motion.p
                className="hidden md:block"
                style={{
                  fontFamily:"'Outfit', sans-serif",
                  fontSize:'0.82rem', fontWeight:300,
                  color:'rgba(200,230,255,0.7)',
                  lineHeight:1.75, maxWidth:380, marginBottom:4,
                }}
                initial={{ opacity:0, y:18 }}
                animate={inView ? { opacity:1, y:0 } : {}}
                transition={{ duration:0.9, ease:expo, delay:0.45 }}
              >
                Two flagship programmes — International and Indian — created to recognize and celebrate leaders at every stage of their success journey.
              </motion.p>
              {/* Desc para - mobile */}
              <motion.p
                className="md:hidden"
                style={{
                  fontFamily:"'Outfit', sans-serif",
                  fontSize:'0.82rem', fontWeight:300,
                  color:'rgba(200,230,255,0.7)',
                  lineHeight:1.75, maxWidth:380, marginBottom:4,
                }}
                initial={{ opacity:0, y:18 }}
                animate={inView ? { opacity:1, y:0 } : {}}
                transition={{ duration:0.9, ease:expo, delay:0.45 }}
              >
                Two flagship programmes — International and Indian — created to recognize and celebrate leaders at every stage of their success journey.
              </motion.p>
            </div>
          </div>

          {/* ════ Cards — 3 in one row, square ════ */}
          <motion.div
            className="cards-row"
            variants={{ hidden:{}, visible:{ transition:{ staggerChildren:0.2, delayChildren:0.4 } } }}
            initial="hidden" animate={inView ? 'visible' : 'hidden'}
          >
            {/* ══ 01 — Town Hall ══ */}
            <motion.div className="card-col-landscape" variants={cardV}>
              <motion.div
                className="flex items-center gap-3 mb-3"
                variants={{ hidden:{}, visible:{ transition:{ staggerChildren:0.1 } } }}
              >
                <motion.span className="index-num" variants={idxNumV}>01</motion.span>
                <motion.div className="index-hr" variants={idxLineV} />
              </motion.div>

              <div
                className="event-card event-card-landscape"
                onClick={() => window.open('https://site.fotoowl.ai/indusvivahealthsciencespvtltd/gallery/327899?embed=true&cover=true&pass_key=2441')}
                onMouseEnter={() => setHoveredIndex(0)}
                onMouseLeave={() => setHoveredIndex(null)}
              >
                <iframe  
                    className="video-landscape"
                    src="https://player.vimeo.com/video/1209750995?h=240bb204c9&badge=0&autopause=0&player_id=0&app_id=58479&autoplay=1&muted=1&loop=1&controls=0"
                    allow="autoplay; fullscreen; picture-in-picture; clipboard-write; encrypted-media; web-share"
                    referrerPolicy="strict-origin-when-cross-origin"
                    title="Town Hall"
                  />
                <div className="card-click-shield" />
                <div className="card-overlay" />
                <div className="card-shine" />
                <div className="card-content">
                  <div className="card-content-inner">
                    <div className="card-sep" />
                    <h3 className="card-title-sm">Town Hall 2K26</h3>
                    <div className="card-cta-row">
                      <span className="card-cta-text">View Photos</span>
                      <div className="cta-circle">
                        <ArrowUpRight size={14} color="white" strokeWidth={1.5} />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
           
            {/* ══ 02 — International ══ */}
            <motion.div className="card-col-landscape" variants={cardV}>
              <motion.div
                className="flex items-center gap-3 mb-3"
                variants={{ hidden:{}, visible:{ transition:{ staggerChildren:0.1 } } }}
              >
                <motion.span className="index-num" variants={idxNumV}>02</motion.span>
                <motion.div className="index-hr" variants={idxLineV} />
              </motion.div>

              <div
                className="event-card event-card-landscape"
                onClick={() => navigate('/international-event')}
                onMouseEnter={() => setHoveredIndex(1)}
                onMouseLeave={() => setHoveredIndex(null)}
              >   
                <iframe
                  className="video-landscape"
                  src="https://player.vimeo.com/video/1171242647?badge=0&autopause=0&player_id=0&app_id=58479&autoplay=1&muted=1&loop=1&controls=0"
                  allow="autoplay; fullscreen; picture-in-picture; clipboard-write; encrypted-media; web-share"
                  referrerPolicy="strict-origin-when-cross-origin"
                  title="International"
                />
                <div className="card-click-shield" />
                <div className="card-overlay" />
                <div className="card-shine" />
                <div className="card-content">
                  <div className="card-content-inner">
                    <div className="card-sep" />
                    <h3 className="card-title-sm">International Events</h3>
                    <div className="card-cta-row">
                      <span className="card-cta-text">View Programme</span>
                      <div className="cta-circle">
                        <ArrowUpRight size={14} color="white" strokeWidth={1.5} />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
   
            {/* ══ 03 — Indian ══ */}
            <motion.div className="card-col-reel" variants={cardV}>
              <motion.div
                className="flex items-center gap-3 mb-3"
                variants={{ hidden:{}, visible:{ transition:{ staggerChildren:0.1 } } }}
              >
                <motion.span className="index-num" variants={idxNumV}>03</motion.span>
                <motion.div className="index-hr" variants={idxLineV} />
              </motion.div>

              <div
                className="event-card event-card-reel"
                onClick={() => navigate('/indian-event')}
                onMouseEnter={() => setHoveredIndex(2)}
                onMouseLeave={() => setHoveredIndex(null)}
              >
                <div className="video-reel-wrap">
                  <iframe
                    src="https://player.vimeo.com/video/1170919831?badge=0&autopause=0&player_id=0&app_id=58479&autoplay=1&muted=1&loop=1&controls=0"
                    allow="autoplay; fullscreen; picture-in-picture; clipboard-write; encrypted-media; web-share"
                    referrerPolicy="strict-origin-when-cross-origin"
                    title="India 1080"
                  />
                </div>
                <div className="card-click-shield" />
                <div className="card-overlay" />
                <div className="card-shine" />
                <div className="card-content">
                  <div className="card-content-inner">
                    <div className="card-sep" />
                    <h3 className="card-title-sm">National Events</h3>
                    <div className="card-cta-row">
                      <span className="card-cta-text">View Programme</span>
                      <div className="cta-circle">
                        <ArrowUpRight size={14} color="white" strokeWidth={1.5} />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>

          </motion.div>

        </div>
      </section>

      <Footer />
      <BackToTop />
    </div>
  );
};

export default Events;